import { existsSync, readFileSync } from 'fs';
import { execSync } from 'child_process';
import { join } from 'path';

// =============================================================================
// Types
// =============================================================================

export type PackageManager = 'bun' | 'npm' | 'yarn' | 'pnpm';

export interface FrameworkDetection {
  name: string;
  displayName: string;
  npmPackage: string;
  mutagentPackage?: string;
}

// =============================================================================
// Framework Detection Map
// =============================================================================

/**
 * Maps package.json dependency names to framework info.
 * Order matters: more specific packages should come first.
 */
export const FRAMEWORK_DETECTION_MAP: Record<string, FrameworkDetection> = {
  '@mastra/core': {
    name: 'mastra',
    displayName: 'Mastra',
    npmPackage: '@mastra/core',
  },
  '@langchain/langgraph': {
    name: 'langgraph',
    displayName: 'LangGraph',
    npmPackage: '@langchain/langgraph',
    mutagentPackage: '@mutagent/langgraph',
  },
  'langchain': {
    name: 'langchain',
    displayName: 'LangChain',
    npmPackage: 'langchain',
    mutagentPackage: '@mutagent/langchain',
  },
  '@langchain/core': {
    name: 'langchain',
    displayName: 'LangChain',
    npmPackage: '@langchain/core',
    mutagentPackage: '@mutagent/langchain',
  },
  '@ai-sdk/core': {
    name: 'vercel-ai',
    displayName: 'Vercel AI SDK',
    npmPackage: '@ai-sdk/core',
    mutagentPackage: '@mutagent/vercel-ai',
  },
  'ai': {
    name: 'vercel-ai',
    displayName: 'Vercel AI SDK',
    npmPackage: 'ai',
    mutagentPackage: '@mutagent/vercel-ai',
  },
  '@google/genai': {
    name: 'generic',
    displayName: 'Google GenAI',
    npmPackage: '@google/genai',
  },
  'openai': {
    name: 'openai',
    displayName: 'OpenAI SDK',
    npmPackage: 'openai',
    mutagentPackage: '@mutagent/openai',
  },
};

// =============================================================================
// Helpers
// =============================================================================

/**
 * Detect the package manager by scanning lockfiles in cwd
 */
export function detectPackageManager(cwd: string = process.cwd()): PackageManager {
  if (existsSync(join(cwd, 'bun.lockb')) || existsSync(join(cwd, 'bun.lock'))) {
    return 'bun';
  }
  if (existsSync(join(cwd, 'pnpm-lock.yaml'))) {
    return 'pnpm';
  }
  if (existsSync(join(cwd, 'yarn.lock'))) {
    return 'yarn';
  }
  if (existsSync(join(cwd, 'package-lock.json'))) {
    return 'npm';
  }

  // Fallback: check if bun is available
  try {
    execSync('bun --version', { stdio: 'ignore' });
    return 'bun';
  } catch {
    return 'npm';
  }
}

/**
 * Build an install command for the given package manager and packages
 */
export function getInstallCommand(pm: PackageManager, packages: string[]): string {
  const pkgList = packages.join(' ');
  const commands: Record<PackageManager, string> = {
    bun: `bun add ${pkgList}`,
    npm: `npm install ${pkgList}`,
    yarn: `yarn add ${pkgList}`,
    pnpm: `pnpm add ${pkgList}`,
  };
  return commands[pm];
}

/**
 * Detect framework from package.json dependencies in cwd
 */
export function detectFrameworkFromPackageJson(cwd: string = process.cwd()): FrameworkDetection | null {
  const pkgPath = join(cwd, 'package.json');
  if (!existsSync(pkgPath)) {
    return null;
  }

  let pkg: { dependencies?: Record<string, string>; devDependencies?: Record<string, string> };
  try {
    pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as typeof pkg;
  } catch {
    return null;
  }

  const allDeps = {
    ...pkg.dependencies,
    ...pkg.devDependencies,
  };

  // Check detection map in order (more specific first)
  for (const [depName, framework] of Object.entries(FRAMEWORK_DETECTION_MAP)) {
    if (depName in allDeps) {
      return framework;
    }
  }

  return null;
}

/**
 * Check if .mutagentrc.json already exists in cwd
 */
export function hasRcConfig(cwd: string = process.cwd()): boolean {
  return existsSync(join(cwd, '.mutagentrc.json'));
}
